import type { ClassificationRule, ClassificationRuleOverride } from "./types";
import { RULE_DESCRIPTORS } from "./rules";

/**
 * Per-user overrides for classification rules. We persist only the deltas
 * (which rules the user toggled off or re-weighted); defaults always come
 * from the descriptor list. When we ship new rules in a future version,
 * users get them automatically without resetting their tweaks.
 */

const KEY_OVERRIDES = "cogseven.toolkit.regions.classifierOverrides";

export type RuleOverrides = Record<string, ClassificationRuleOverride>;

export async function loadOverrides(): Promise<RuleOverrides> {
  return readOverrides();
}

export async function saveOverrides(overrides: RuleOverrides): Promise<void> {
  localStorage.setItem(KEY_OVERRIDES, JSON.stringify(sanitize(overrides)));
}

export async function resetOverrides(): Promise<void> {
  localStorage.removeItem(KEY_OVERRIDES);
}

/** Load defaults + overrides, returning the effective rule set. */
export async function loadEffectiveRules(): Promise<ClassificationRule[]> {
  return effectiveRulesFromOverrides(readOverrides());
}

/**
 * Synchronous variant for paths that can't await — currently used by the
 * scanner.
 */
export function loadEffectiveRulesSync(): ClassificationRule[] {
  return effectiveRulesFromOverrides(readOverrides());
}

function effectiveRulesFromOverrides(overrides: RuleOverrides): ClassificationRule[] {
  return RULE_DESCRIPTORS.map((d) => {
    const ov = overrides[d.id] ?? {};
    return {
      ...d,
      enabled: ov.enabled !== undefined ? ov.enabled : d.defaultEnabled,
      weight: typeof ov.weight === "number" && ov.weight >= 0 ? ov.weight : d.defaultWeight,
    };
  });
}

function readOverrides(): RuleOverrides {
  const raw = localStorage.getItem(KEY_OVERRIDES);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return sanitize(parsed as RuleOverrides);
  } catch {
    return {};
  }
}

function sanitize(overrides: RuleOverrides): RuleOverrides {
  const validIds = new Set(RULE_DESCRIPTORS.map((d) => d.id));
  const out: RuleOverrides = {};
  for (const [id, ov] of Object.entries(overrides)) {
    if (!validIds.has(id)) continue;
    if (!ov || typeof ov !== "object") continue;
    const entry: ClassificationRuleOverride = {};
    if (typeof ov.enabled === "boolean") entry.enabled = ov.enabled;
    if (typeof ov.weight === "number" && Number.isFinite(ov.weight) && ov.weight >= 0) {
      entry.weight = ov.weight;
    }
    if (entry.enabled !== undefined || entry.weight !== undefined) {
      out[id] = entry;
    }
  }
  return out;
}
